/**
 * Utilidades para detectar la empresa desde la URL
 */

const SLUG_REGEX = /^[a-z0-9-]{1,63}$/i;

/**
 * Primer segmento de rutas de la app. Siempre en minúsculas:
 * sanitizeCompanySlug() lowercased el path, y 'huespedesActivos' no coincidía
 * con la lista en camelCase → se tomaba como slug de empresa y cerraba la sesión.
 */
const SYSTEM_PATH_SEGMENTS = new Set([
  'auth',
  'api',
  'home',
  'usuarios',
  'huespedes',
  'huespedesactivos',
  'check-in',
  'habitaciones',
  'calendario',
  'estadisticas',
  'configuracion',
  'suscripcion',
  'services',
  'mi-cuenta',
]);

export function sanitizeCompanySlug(rawSlug) {
  if (!rawSlug || typeof rawSlug !== 'string') return null;
  const trimmed = rawSlug.trim().toLowerCase();
  if (!SLUG_REGEX.test(trimmed)) {
    return null;
  }
  return trimmed;
}

export function isSystemPathSegment(segment) {
  if (!segment || typeof segment !== 'string') return false;
  return SYSTEM_PATH_SEGMENTS.has(segment.trim().toLowerCase());
}

/**
 * Extrae el slug solo desde la URL visible (subdominio o primer segmento de ruta).
 * No usa localStorage: sirve para saber si la barra de direcciones ya lleva el slug.
 */
export function getCompanySlugFromPath() {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  const subdomainMatch = hostname.match(/^([^.]+)\./);
  if (subdomainMatch && subdomainMatch[1] !== 'www' && subdomainMatch[1] !== 'app') {
    const slug = sanitizeCompanySlug(subdomainMatch[1]);
    if (!slug || isSystemPathSegment(slug)) return null;
    return slug;
  }

  const pathMatch = pathname.match(/^\/([^/]+)/);
  if (pathMatch && pathMatch[1]) {
    const potentialSlug = sanitizeCompanySlug(pathMatch[1]);
    if (potentialSlug && !isSystemPathSegment(potentialSlug)) {
      return potentialSlug;
    }
  }

  return null;
}

/**
 * En app.hospedapp.cl el slug debe verse en la ruta.
 * Si la URL está en /home (sin slug) pero hay slug guardado, redirige a /{slug}/home.
 * Esto es crítico en iPhone: "Agregar a inicio" guarda la URL de la barra.
 * @returns {boolean} true si se disparó una redirección
 */
export function ensureSlugInAppPath(forcedSlug) {
  if (typeof window === 'undefined') return false;

  const hostname = window.location.hostname;
  const needsSlugInPath =
    hostname === 'app.hospedapp.cl' || hostname.includes('app.hospedapp.cl');
  if (!needsSlugInPath) return false;

  if (getCompanySlugFromPath()) return false;

  const slug =
    sanitizeCompanySlug(forcedSlug) ||
    readStoredCompanySlug() ||
    sanitizeCompanySlug(getCompanySlugFromUrl());
  if (!slug) return false;

  const { pathname, search, hash } = window.location;
  const nextPath = pathname === '/' ? `/${slug}/home` : `/${slug}${pathname}`;
  window.location.replace(`${nextPath}${search || ''}${hash || ''}`);
  return true;
}

/**
 * Extrae el slug de la empresa desde la URL
 * Soporta tres formatos (prioridad: subdominio > ruta > localStorage):
 * 1. Subdominio: santiago.hospedapp.cl -> "santiago" (RECOMENDADO)
 * 2. Ruta: app.hospedapp.cl/santiago/home -> "santiago" (para producción con dominio único)
 * 3. Ruta: www.hospedapp.cl/santiago -> "santiago" (fallback para desarrollo)
 * 4. localStorage: Si ya se detectó antes, usar el guardado (especialmente útil en /auth/login)
 * 
 * IMPORTANTE: 
 * - Con subdominios, el slug NO aparece en las rutas internas.
 *   Ejemplo: santiago.hospedapp.cl/home (slug está en el dominio, no en la ruta)
 * - Con app.hospedapp.cl, el slug SÍ aparece en las rutas.
 *   Ejemplo: app.hospedapp.cl/santiago/home (slug está en la ruta)
 */
export function getCompanySlugFromUrl() {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  
  // Verificar si estamos en una ruta de autenticación donde el slug no está en la URL
  const isAuthRoute = pathname.startsWith('/auth/') || pathname === '/auth' || pathname === '/';

  // Opción 1: Detectar por subdominio (ej: santiago.hospedapp.cl)
  // Esta es la forma RECOMENDADA y más profesional
  const subdomainMatch = hostname.match(/^([^.]+)\./);
  // Excluir 'www' (dominio principal) y 'app' (portal de superadmin/cliente)
  // 'app.hospedapp.cl' es la URL para el portal de cliente en producción
  if (subdomainMatch && subdomainMatch[1] !== 'www' && subdomainMatch[1] !== 'app') {
    const slug = sanitizeCompanySlug(subdomainMatch[1]);
    if (!slug || isSystemPathSegment(slug)) {
      return null;
    }
    saveCompanySlug(slug);
    return slug;
  }

  // Opción 2: Detectar por ruta cuando el dominio es app.hospedapp.cl
  // En producción, el dominio es app.hospedapp.cl y el slug está en la ruta
  // Ejemplo: app.hospedapp.cl/santiago/home -> slug: "santiago"
  // PRIORIDAD: Siempre intentar detectar desde la URL actual primero
  if (hostname === 'app.hospedapp.cl' || hostname.includes('app.hospedapp.cl')) {
    const pathMatch = pathname.match(/^\/([^/]+)/);
    if (pathMatch && pathMatch[1]) {
      const potentialSlug = sanitizeCompanySlug(pathMatch[1]);
      if (potentialSlug && !isSystemPathSegment(potentialSlug)) {
        saveCompanySlug(potentialSlug);
        return potentialSlug;
      }
    }
    
    // Si estamos en una ruta de autenticación y no se detectó slug de la ruta,
    // intentar obtenerlo de localStorage (útil cuando el usuario accede directamente a /auth/login)
    // PERO solo si realmente no hay slug en la URL
    if (isAuthRoute) {
      const storedSlug = readStoredCompanySlug();
      if (storedSlug) {
        return storedSlug;
      }
    }
  }

  // Opción 3: Detectar por ruta (ej: www.hospedapp.cl/santiago o localhost:3000/santiago)
  // Solo funciona si el slug está en la primera parte de la ruta
  // Útil para desarrollo local o si no se pueden configurar subdominios
  const pathMatch = pathname.match(/^\/([^/]+)/);
  if (pathMatch && pathMatch[1]) {
    const potentialSlug = sanitizeCompanySlug(pathMatch[1]);
    if (potentialSlug && !isSystemPathSegment(potentialSlug)) {
      saveCompanySlug(potentialSlug);
      return potentialSlug;
    }
  }

  // Opción 4: Detectar desde localStorage (si ya se detectó antes)
  // Útil cuando el usuario navega entre páginas y el slug no está en la URL actual
  // También útil cuando el usuario accede directamente a /auth/login
  const storedSlug = readStoredCompanySlug();
  if (storedSlug) {
    return storedSlug;
  }

  return null;
}

function readStoredCompanySlug() {
  const storedSlug = sanitizeCompanySlug(localStorage.getItem('detectedCompanySlug'));
  if (storedSlug && !isSystemPathSegment(storedSlug)) {
    return storedSlug;
  }
  return null;
}

/**
 * Guarda el slug detectado en localStorage
 */
export function saveCompanySlug(slug) {
  const sanitized = sanitizeCompanySlug(slug);
  if (sanitized && !isSystemPathSegment(sanitized)) {
    localStorage.setItem('detectedCompanySlug', sanitized);
  } else if (!sanitized) {
    localStorage.removeItem('detectedCompanySlug');
  }
}

/**
 * Limpia el slug guardado
 */
export function clearCompanySlug() {
  localStorage.removeItem('detectedCompanySlug');
}
